/**
 * linearityUtils.js
 *
 * Shared utilities for linearity/projection cards in the Revenue Command Center.
 * These functions transform API responses into chart-ready data structures.
 *
 * Used by:
 * - LinearityComparisonCard (quarterly)
 * - MonthlyLinearityComparisonCard (monthly)
 * - TeamWiseCurrentQtrProjeectionCard (quarterly teams)
 * - TeamWiseCurrentMonthProjectionCard (monthly teams)
 *
 * Data Flow:
 * 1. API returns raw linearity data with period identifiers (quarter or month)
 * 2. transformLinearityData() converts it to chart format
 * 3. D3Linearity component renders the transformed data
 */

import {
  getDefaultSalesType,
  getDefaultSelectedFiscalQuarters,
  getDefaultThreshold,
} from "../../util/customFunctions";

/**
 * Transform linearity data for multi-period comparison charts.
 * Works for both quarterly and monthly data - the structure is the same.
 *
 * @param {Array} linearityData - Array of period data from API
 *   Expected shape: { quarter/month, wonAsOfCuurentDayOfQuarter, eventuallyWonInQuarter, quota, totalACV, totalACVLOW, totalACVHIGH }
 * @param {string} currentPeriod - Current fiscal period identifier (e.g., "FQ-2025-Q1" or "2025-01")
 * @returns {Array} Chart-ready data array with shape:
 *   { quarter, quota, wonEOQ, wonQTD, isCurrentQtr, qtrProjectionMid, qtrProjectionHigh }
 */
export function transformLinearityData(linearityData, currentPeriod) {
  if (!linearityData || !Array.isArray(linearityData)) {
    return [];
  }

  return linearityData.map((item) => {
    // Extract period identifier - API uses "quarter" field for both quarterly and monthly data
    const periodId = item.quarter || item.month;
    const wonQTD = item.wonAsOfCuurentDayOfQuarter || 0;
    const quota = item.quota || 0;
    const isCurrentPeriod = periodId === currentPeriod;

    // Calculate wonEOQ (Eventually Won in Period)
    // This is the amount expected to close by end of period, minus what was already won
    let wonEOQ =
      item.eventuallyWonInQuarter > 0
        ? item.eventuallyWonInQuarter
        : (item.totalACVLOW || 0) - wonQTD;

    let qtrProjectionMid = null;
    let qtrProjectionHigh = null;

    // Current period gets projection bands (Low/Mid/High)
    if (isCurrentPeriod) {
      const midTotal = item.totalACV ?? 0;
      const highTotal = item.totalACVHIGH ?? 0;
      const lowTotal = item.totalACVLOW ?? 0;

      // Projection segments are deltas between tiers
      qtrProjectionMid = Math.max(0, midTotal - lowTotal);
      qtrProjectionHigh = Math.max(0, highTotal - Math.max(midTotal, lowTotal));

      // For current period, EOQ is remainder from low projection
      wonEOQ = Math.max(0, lowTotal - wonQTD);
    }

    return {
      // Use "quarter" as field name for D3Linearity compatibility (x-axis label)
      quarter: periodId,
      quota,
      wonEOQ,
      wonQTD,
      isCurrentQtr: isCurrentPeriod,
      qtrProjectionMid,
      qtrProjectionHigh,
    };
  });
}

/**
 * Transform single period data for team-wise projection charts.
 * Used when showing projections broken down by team for current period only.
 *
 * @param {Object} periodData - Single period data object from API
 * @param {string} teamName - Team identifier to label the data point
 * @returns {Object|null} Chart-ready team data object, or null if invalid input
 */
export function transformTeamLinearityData(periodData, teamName) {
  if (!periodData) return null;

  const wonQTD = periodData.wonAsOfCuurentDayOfQuarter || 0;
  const quota = periodData.quota || 0;
  const midTotal = periodData.totalACV ?? 0;
  const highTotal = periodData.totalACVHIGH ?? 0;
  const totalACVLOW = periodData.totalACVLOW ?? 0;

  // Calculate projection deltas (same logic as transformLinearityData)
  const qtrProjectionMid = Math.max(0, midTotal - totalACVLOW);
  const qtrProjectionHigh = Math.max(
    0,
    highTotal - Math.max(midTotal, totalACVLOW)
  );
  const wonEOQ = Math.max(0, totalACVLOW - wonQTD);

  return {
    team: teamName,
    quota,
    wonEOQ,
    wonQTD,
    isCurrentQtr: true, // Always true since we only show current period for team charts
    qtrProjectionMid,
    qtrProjectionHigh,
    qtrProjectionLow: totalACVLOW, // Include low projection for reference
  };
}

/**
 * Build default filters for Time Series Prediction APIs.
 * Handles the common filter construction logic for both quarterly and monthly endpoints.
 *
 * @param {Object} filterResponse - API filter response (from getTimeSeriesPredictionv2Filters or getTimeSeriesPredictionByMonthFilters)
 * @param {Object} options - Configuration options
 * @param {string} options.periodType - "quarter" or "month" - determines filter field names
 * @param {number} options.lookbackPeriods - Number of lookback periods (default: 4)
 * @returns {Object} { filters: Array, defaultPeriod: string } - Filter array ready for data API
 */
export function buildDefaultFilters(filterResponse, options = {}) {
  const { periodType = "quarter", lookbackPeriods = 4 } = options;
  const message = filterResponse.message || filterResponse;

  // Determine field names based on period type
  // Quarterly uses: fiscal_quarter, lookback_quarter
  // Monthly uses: fiscal_month, lookback_month
  const periodFieldName =
    periodType === "month" ? "fiscal_month" : "fiscal_quarter";
  const lookbackFieldName =
    periodType === "month" ? "lookback_month" : "lookback_quarter";

  // Get default period selection
  // For quarterly: uses fiscalQuarters array
  // For monthly: uses fiscalMonths array
  const periodsArray =
    periodType === "month" ? message.fiscalMonths : message.fiscalQuarters;
  const defaultPeriod = getDefaultSelectedFiscalQuarters(
    periodsArray,
    periodFieldName
  );

  let filters = [];

  // Add period filter
  if (defaultPeriod && defaultPeriod.length > 0) {
    filters.push({
      name: periodFieldName,
      value: defaultPeriod,
    });
  }

  // Add offset days if available
  const defaultDays = message.defaultOffsetDays;
  if (defaultDays) {
    filters.push({
      name: "offset_days",
      value: defaultDays,
    });
  }

  // Add lookback period
  filters.push({
    name: lookbackFieldName,
    value: lookbackPeriods,
  });

  // Process boolean flags with "All" -> "No" conversion
  const excludeLargeDeals = normalizeFilterValue(
    getDefaultSalesType(message.exclude_large_deals, "exclude_large_deals")
  );

  const includeAllCQ = normalizeFilterValue(
    getDefaultSalesType(message.includeCQWon, "INCLUDE_ALL_CQ_WON")
  );

  // IMPORTANT:
  // The legacy monthly dashboard (TimeSeriesPredictionByMonth) does NOT send
  // `exclude_acv_lte_zero`, while the quarterly dashboard does.
  // To keep RCC cards aligned with legacy behavior, only include this filter for quarterly.
  let excludeACVLTEZero = "No";
  if (periodType === "quarter" && message.exclude_acv_lte_zero) {
    excludeACVLTEZero = normalizeFilterValue(
      getDefaultSalesType(message.exclude_acv_lte_zero, "exclude_acv_lte_zero")
    );
  }

  filters.push({ name: "INCLUDE_ALL_CQ_WON", value: includeAllCQ });
  if (periodType === "quarter") {
    filters.push({ name: "exclude_acv_lte_zero", value: excludeACVLTEZero });
  }

  // Add default team filter if provided and not "All"
  const defaultTeam = message?.parentChildrenTeamFilter?.defaultValueTeam;
  if (Array.isArray(defaultTeam) && defaultTeam.length > 0) {
    const firstTeam = String(defaultTeam[0] ?? "").toLowerCase();
    if (firstTeam && firstTeam !== "all") {
      filters.push({ name: "team", value: defaultTeam });
    }
  }

  return {
    filters,
    defaultPeriod: defaultPeriod?.[0] || null,
  };
}

/**
 * Normalize filter values - converts "All" to "No" for boolean-like filters.
 * This matches the existing pattern in the codebase where "All" means no filter applied.
 *
 * @param {Array} filterValue - Array from getDefaultSalesType
 * @returns {string} Normalized value ("Yes" or "No")
 */
function normalizeFilterValue(filterValue) {
  if (!filterValue || filterValue[0] === "All") {
    return "No";
  }
  return filterValue[0];
}
